import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { stateMasterAPI, masterDataAPI } from "../services/api";
import Swal from "sweetalert2";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditStateMaster.css";
import { getRoutePermissions } from "../utils/auth";

const normalizeCountry = (item) => ({
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  countryName: item.countryName || item.CountryName || item.COUNTRYNAME || "",
});

const normalizeState = (item) => ({
  stateCode: item.stateCode || item.StateCode || item.STATECODE || "",
  stateName: item.stateName || item.StateName || item.STATENAME || "",
  countryCode: item.countryCode || item.CountryCode || item.COUNTRYCODE || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditStateMaster = () => {
  const navigate = useNavigate();
  const { stateCode } = useParams();
  const isEditMode = !!stateCode;

  const routePermissions = getRoutePermissions("/states");
  const { canAdd, canEdit } = routePermissions;
  const canSave = isEditMode ? canEdit : canAdd;

  const [state, setState] = useState({
    stateCode: "",
    stateName: "",
    countryCode: "",
    isActive: true,
  });

  const [dropdowns, setDropdowns] = useState({
    countries: [],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const countrySelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canSave) {
      Swal.fire({
        title: "Access Denied",
        text: `You do not have permission to ${isEditMode ? "edit" : "add"} states.`,
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/states"));
      return;
    }
    loadDropdowns();
    if (isEditMode) {
      loadState();
    } else {
      loadNextStateCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateCode, canSave]);

  // Initialize Select2 for Country dropdown
  useEffect(() => {
    if (dropdowns.countries.length === 0) {
      return;
    }

    if (countrySelectRef.current) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        $countrySelect.select2("destroy");
      }
      $countrySelect.select2({
        width: "100%",
        placeholder: "Select Country",
        allowClear: false,
      });
      $countrySelect.on("select2:select", function (e) {
        handleChange("countryCode", e.params.data.id);
      });
      if (state.countryCode) {
        $countrySelect.val(state.countryCode).trigger("change");
      }
    }

    return () => {
      if (countrySelectRef.current) {
        const $select = $(countrySelectRef.current);
        if ($select.data("select2")) {
          $select.off("select2:select");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdowns.countries]);

  // Update Select2 value when state changes
  useEffect(() => {
    if (countrySelectRef.current && dropdowns.countries.length > 0) {
      const $countrySelect = $(countrySelectRef.current);
      if ($countrySelect.data("select2")) {
        const currentVal = $countrySelect.val();
        if (currentVal !== (state.countryCode || "")) {
          $countrySelect.val(state.countryCode || "").trigger("change");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.countryCode]);

  const loadDropdowns = async () => {
    try {
      setLoading(true);
      const countryRes = await masterDataAPI.getCountries();

      setDropdowns({
        countries: (countryRes?.data || []).map((c) => normalizeCountry(c)),
      });
    } catch (e) {
      console.error("Error loading dropdowns:", e);
      setError("Failed to load dropdown data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const loadNextStateCode = async () => {
    try {
      const response = await stateMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setState((prev) => ({ ...prev, stateCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next state code:", e);
      setState((prev) => ({ ...prev, stateCode: "ST00001" }));
    }
  };

  const loadState = async () => {
    try {
      setLoading(true);
      const response = await stateMasterAPI.getByCode(stateCode);
      if (response && response.success && response.data) {
        setState(normalizeState(response.data));
      } else {
        navigate("/states");
      }
    } catch (e) {
      console.error("Error loading state:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load state details", "error");
      }
      navigate("/states");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!state.stateCode?.trim()) {
      Swal.fire("Error", "State ID is required", "error");
      return false;
    }
    if (!state.stateName?.trim()) {
      Swal.fire("Error", "State Name is required", "error");
      return false;
    }
    if (!state.countryCode) {
      Swal.fire("Error", "Country is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await stateMasterAPI.update(
          stateCode,
          state,
          createdBy
        );
      } else {
        response = await stateMasterAPI.create(state, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text: response.message || "State saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/states");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save state",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving state:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save state. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/states"
            onClick={(e) => {
              e.preventDefault();
              navigate("/states");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            State Master
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit State" : "Add State"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/states")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">
                  Country <span style={{ color: "red" }}>*</span>
                </label>
                <select
                  ref={countrySelectRef}
                  className="form-control select2-country"
                  required
                >
                  <option value="">Select Country</option>
                  {dropdowns.countries.map((c) => (
                    <option key={c.countryCode} value={c.countryCode}>
                      {c.countryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  State ID <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={state.stateCode}
                  required
                  fullWidth
                  disabled={true}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  State Name <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={state.stateName}
                  onChange={(e) => handleChange("stateName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter state name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/states")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !canSave}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditStateMaster;



